import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
  SvgIcon,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import heroImage from '@/assets/hero.png';
import vmIcon from '@/assets/vm-icon.png';
import { useAuth } from '@/shared/auth/useAuth';
import { buildGuestLoginUrl } from '@/shared/auth/cognito';
import { UserMenu } from '@/shared/ui/UserMenu';

const iconPaths = {
  arrow: 'M12 4l-1.41 1.41L15.17 10H4v2h11.17l-4.58 4.59L12 18l7-7-7-7z',
  bolt: 'M11 21h-1l1-7H7.5a1 1 0 0 1-.9-1.45l5-10A1 1 0 0 1 13.5 3h1l-1 7H17a1 1 0 0 1 .9 1.45l-5 10A1 1 0 0 1 11 21z',
  headphones:
    'M12 3a8 8 0 0 0-8 8v6a3 3 0 0 0 3 3h2v-8H6v-1a6 6 0 0 1 12 0v1h-3v8h2a3 3 0 0 0 3-3v-6a8 8 0 0 0-8-8z',
  music:
    'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  qr: 'M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2zm-6 4h2v4h-2v-4zm4 2h4v2h-4v-2z',
  wave:
    'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
};

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return (
    <SvgIcon {...props}>
      <path d={path} />
    </SvgIcon>
  );
}

const requestRows = [
  { title: 'Levitating', artist: 'Dua Lipa', votes: 24, color: 'secondary.main' },
  { title: 'Blinding Lights', artist: 'The Weeknd', votes: 18, color: 'primary.light' },
  { title: 'As It Was', artist: 'Harry Styles', votes: 12, color: 'success.main' },
];

const featureTiles = [
  {
    icon: iconPaths.qr,
    title: 'Scan',
    copy: 'Guests jump into the event from the floor.',
    color: 'primary.light',
  },
  {
    icon: iconPaths.headphones,
    title: 'Curate',
    copy: 'DJs approve, park, or play requests fast.',
    color: 'secondary.light',
  },
  {
    icon: iconPaths.music,
    title: 'Flow',
    copy: 'Spotify-friendly control for every set.',
    color: 'success.light',
  },
];

const waveform = [34, 18, 44, 28, 52, 22, 62, 38, 50, 26, 58, 32, 46, 20, 40, 30, 54, 24];

export function LandingPage() {
  const { isAuthenticated, login, user } = useAuth();
  const theme = useTheme();

  const isGuest = user?.role === 'GUEST';
  const isDj = user?.role === 'DJ' || user?.role === 'ADMIN';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: 'common.white',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'primary.dark',
        backgroundImage: `
          linear-gradient(120deg, ${alpha(theme.palette.primary.dark, 0.98)} 0%, ${alpha(theme.palette.text.primary, 0.95)} 52%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%),
          linear-gradient(118deg, transparent 0 48%, ${alpha(theme.palette.secondary.light, 0.11)} 48% 53%, transparent 53%),
          repeating-linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.07)} 0 1px, transparent 1px 18px)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: { xs: -92, md: -120 },
          right: { xs: -170, sm: -120, md: -90 },
          left: { xs: '34%', md: '18%' },
          height: { xs: 230, md: 320 },
          pointerEvents: 'none',
          background: `linear-gradient(105deg, transparent 0 18%, ${alpha(theme.palette.warning.light, 0.18)} 18% 27%, transparent 27% 42%, ${alpha(theme.palette.secondary.light, 0.2)} 42% 53%, transparent 53%)`,
          transform: { xs: 'skewY(-8deg)', md: 'skewY(-5deg)' },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 2, md: 3 } }}>
        <Stack
          component="header"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ minHeight: { xs: 58, sm: 66 } }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <Box
              component="img"
              src={vmIcon}
              alt=""
              aria-hidden="true"
              sx={{
                width: { xs: 42, sm: 46 },
                height: { xs: 42, sm: 46 },
                borderRadius: '34% 46% 36% 44%',
                display: 'block',
                transform: 'rotate(-3deg)',
                boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 14px 30px ${alpha(theme.palette.secondary.main, 0.28)}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography component="p" sx={{ fontSize: { xs: 18, sm: 21 }, lineHeight: 1, fontWeight: 900, letterSpacing: 0 }}>
                Vibe Masters
              </Typography>
              <Typography component="p" sx={{ mt: 0.45, color: alpha(theme.palette.common.white, 0.68), fontSize: 12, lineHeight: 1, fontWeight: 700 }}>
                Crowd-powered DJ sets
              </Typography>
            </Box>
          </Stack>

          {isAuthenticated ? (
            <UserMenu tone="dark" />
          ) : (
            <Button
              onClick={login}
              variant="outlined"
              size="medium"
              sx={{
                minWidth: { xs: 96, sm: 118 },
                minHeight: { xs: 40, sm: 44 },
                color: 'common.white',
                borderColor: alpha(theme.palette.common.white, 0.42),
                '&:hover': {
                  borderColor: 'secondary.light',
                  bgcolor: alpha(theme.palette.secondary.main, 0.16),
                },
              }}
            >
              DJ Login
            </Button>
          )}
          {/* DJ Login is intentionally hidden for guest users — UserMenu above handles logout */}
        </Stack>

        <Box
          component="main"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(380px, 1.08fr)' },
            gap: { xs: 4, md: 6, lg: 8 },
            alignItems: 'center',
            minHeight: { md: 'calc(100vh - 96px)' },
            pt: { xs: 4, sm: 5, md: 2.5 },
            pb: { xs: 5, md: 3 },
          }}
        >
          <Stack spacing={{ xs: 2.25, md: 2.75 }} sx={{ maxWidth: 650 }}>
            <Chip
              icon={<AppIcon path={iconPaths.bolt} />}
              label="Live requests. Less admin. Better drops."
              sx={{
                alignSelf: 'flex-start',
                height: 36,
                color: 'warning.light',
                border: `1px solid ${alpha(theme.palette.warning.light, 0.5)}`,
                bgcolor: alpha(theme.palette.warning.main, 0.11),
                '& .MuiChip-icon': { color: 'warning.light' },
                '& .MuiChip-label': { px: 1, fontWeight: 800 },
              }}
            />

            <Stack spacing={1.7}>
              <Typography
                variant="h1"
                sx={{
                  maxWidth: 620,
                  fontSize: { xs: 44, sm: 64, md: 78, lg: 88 },
                  lineHeight: { xs: 0.98, md: 0.92 },
                  letterSpacing: 0,
                  textWrap: 'balance',
                }}
              >
                Make every set feel alive.
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 555,
                  color: alpha(theme.palette.common.white, 0.76),
                  fontSize: { xs: 17, sm: 19, md: 21 },
                  lineHeight: 1.55,
                  fontWeight: 500,
                }}
              >
                Let the crowd request, keep the DJ in control, and make every request part of the show.
              </Typography>
            </Stack>

            {isGuest && (
              <Typography sx={{ color: alpha(theme.palette.common.white, 0.6), fontSize: 15, fontWeight: 500 }}>
                Welcome back, {user?.firstName ?? user?.displayName ?? 'Guest'} — scan a barcode to jump into a set.
              </Typography>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 0.75 }}>
              {isGuest ? (
                /* Guest CTA — scan a barcode to join an event */
                <Button
                  href="/scan"
                  variant="contained"
                  size="large"
                  startIcon={<AppIcon path={iconPaths.qr} />}
                  sx={{
                    minHeight: { xs: 52, sm: 52 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    bgcolor: 'secondary.main',
                    boxShadow: `0 18px 40px ${alpha(theme.palette.secondary.main, 0.34)}`,
                    '&:hover': { bgcolor: 'secondary.dark', boxShadow: `0 18px 40px ${alpha(theme.palette.secondary.main, 0.42)}` },
                  }}
                >
                  Scan Barcode
                </Button>
              ) : (
                <Button
                  {...(isDj
                    ? { href: '/dj' }
                    : { onClick: () => { window.location.href = buildGuestLoginUrl('/'); } }
                  )}
                  variant="contained"
                  size="large"
                  endIcon={<AppIcon path={iconPaths.arrow} />}
                  sx={{
                    minHeight: { xs: 52, sm: 52 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    bgcolor: 'secondary.main',
                    boxShadow: `0 18px 40px ${alpha(theme.palette.secondary.main, 0.34)}`,
                    '&:hover': { bgcolor: 'secondary.dark', boxShadow: `0 18px 40px ${alpha(theme.palette.secondary.main, 0.42)}` },
                  }}
                >
                  {isDj ? 'Go to Dashboard' : 'Get Started'}
                </Button>
              )}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1,
                pt: { xs: 1, md: 1.75 },
              }}
            >
              {featureTiles.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    p: 1.5,
                    minHeight: 122,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.075),
                    backdropFilter: 'blur(16px)',
                    transition: 'transform 160ms ease, border-color 160ms ease, background-color 160ms ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
                      opacity: 0.72,
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: alpha(theme.palette.secondary.light, 0.32),
                      bgcolor: alpha(theme.palette.common.white, 0.095),
                    },
                  }}
                >
                  <Box sx={{ color: item.color, mb: 1, '& svg': { fontSize: 28 } }}>
                    <AppIcon path={item.icon} />
                  </Box>
                  <Typography sx={{ fontSize: 18, lineHeight: 1.1, fontWeight: 900 }}>{item.title}</Typography>
                  <Typography sx={{ mt: 0.75, color: alpha(theme.palette.common.white, 0.68), fontSize: 13.5, lineHeight: 1.42 }}>
                    {item.copy}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>

          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 520, sm: 600, md: 640 },
              display: 'grid',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={heroImage}
              alt="DJ performing with Vibe Masters"
              sx={{
                position: 'absolute',
                right: { xs: '50%', md: 18 },
                top: { xs: 16, md: 24 },
                transform: { xs: 'translateX(50%)', md: 'none' },
                width: { xs: 310, sm: 400, md: 470 },
                maxWidth: '88vw',
                filter: `drop-shadow(0 34px 58px ${alpha(theme.palette.common.black, 0.42)}) saturate(1.12)`,
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: { xs: '220px 0 auto 0', sm: '278px 22px auto 22px', md: 'auto 0 42px 0' },
                maxWidth: { xs: '100%', md: 620 },
                ml: { md: 'auto' },
                p: { xs: 1.5, sm: 2 },
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
                bgcolor: alpha(theme.palette.text.primary, 0.72),
                boxShadow: `0 28px 70px ${alpha(theme.palette.common.black, 0.38)}`,
                backdropFilter: 'blur(22px)',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '34% 46% 36% 44%',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'common.white',
                      bgcolor: 'primary.main',
                      transform: 'rotate(-4deg)',
                    }}
                  >
                    <AppIcon path={iconPaths.wave} fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 15, lineHeight: 1.1, fontWeight: 900 }}>Tonight's playlist</Typography>
                    <Typography sx={{ mt: 0.25, color: alpha(theme.palette.common.white, 0.6), fontSize: 12, fontWeight: 700 }}>
                      Crowd requests, DJ approved
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label="Live"
                  size="small"
                  sx={{
                    color: 'success.light',
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    border: `1px solid ${alpha(theme.palette.success.light, 0.32)}`,
                  }}
                />
              </Stack>

              <Stack spacing={1}>
                {requestRows.map((row, index) => (
                  <Box
                    key={row.title}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '6px minmax(0, 1fr) auto',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      minHeight: 62,
                      borderRadius: 1,
                      bgcolor: index === 0 ? alpha(theme.palette.secondary.main, 0.2) : alpha(theme.palette.common.white, 0.08),
                      border: `1px solid ${index === 0 ? alpha(theme.palette.secondary.light, 0.34) : alpha(theme.palette.common.white, 0.1)}`,
                    }}
                  >
                    <Box sx={{ width: 6, height: 38, borderRadius: 99, bgcolor: row.color }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1.1, fontWeight: 900 }}>
                        {row.title}
                      </Typography>
                      <Typography noWrap sx={{ mt: 0.4, color: alpha(theme.palette.common.white, 0.6), fontSize: 12.5, lineHeight: 1.1 }}>
                        {row.artist}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: 'warning.light', fontSize: 13, fontWeight: 900 }}>{row.votes} votes</Typography>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 1.75, borderColor: alpha(theme.palette.common.white, 0.12) }} />

              <Stack direction="row" alignItems="end" spacing={0.55} sx={{ height: 72 }}>
                {waveform.map((height, index) => (
                  <Box
                    key={`${height}-${index}`}
                    sx={{
                      flex: '1 1 0',
                      height,
                      minWidth: 4,
                      borderRadius: 99,
                      background:
                        index % 4 === 0
                          ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.main})`
                          : `linear-gradient(180deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
